import { bus } from "@/boot/bus";
import { genqlBuild } from "@/core/graphql/genql/builder";
import { useGraphql } from "@/core/graphql/service";
import { useScopeQuery } from "@/core/store/scopeQuery";
import { ID, Maybe } from "@/core/types";
import { treeBuild } from "@/core/util";
import { arrayIndex } from "@/core/util/arrayIndex";
import { toID } from "@/core/util/toID";
import { toIDS } from "@/core/util/toIDS";
import { ProductIngredient, Product, ProductFlag, ProductSection } from "@/gql/gen";
import useProduct, { ProductModel } from "@/modules/shop/composable/useProduct";
import { CatalogStoreGettersLoader, ProductDescriptor } from "@/modules/shop/store/util/catalog";
import { defineStore } from "pinia";
import { computed, nextTick, ref } from "vue";

const STORE_NAME = "shop-catalog";

export const useCatalogStore = defineStore(STORE_NAME, () => {

    const {registerScopeQuery} = useScopeQuery();
    const {query} = useGraphql()

    const loadGetters: CatalogStoreGettersLoader = () => {
        return {
            sections,
            products,
            productsById,
            productsModelsById,
            resolveProduct,
            resolveProductModel
        }
    }

    const viewMode = ref<'list' | 'grid'>('list');

    // Флаг для глобального индикатора загрузки при смене viewMode
    const isChangingViewMode = ref(false);

    const setViewMode = (value: 'list' | 'grid', delay: number = 150) => {
        // Отправляем событие в bus для показа индикатора
        bus.emit('viewMode:changing', value);

        // Откладываем смену viewMode на указанную задержку
        setTimeout(() => {
            viewMode.value = value;
        }, delay);
    }

    const products = ref<Product[]>([]);
    const sections = ref<ProductSection[]>([]);
    const productIngredients = ref<ProductIngredient[]>([]);
    const productFlags = ref<ProductFlag[]>([]);

    // COMPUTED
    const productsById = computed(() => arrayIndex(products.value, 'ID'))
    const productsModels = computed(() => products.value.map(product => useProduct(product, loadGetters)))
    const productsModelsById = computed(() => arrayIndex(productsModels.value, (item) => item.ID))
    const productsModelsBySection = computed(() => {
        return productsModels.value.reduce<Record<string, ProductModel[]>>((map, product) => {
            if (product.isSaleAllow()) {
                product.IBLOCK_SECTION_IDS.forEach((sectionId) => {
                    if (!map[sectionId]) {
                        map[sectionId] = []
                    }
                    map[sectionId]!.push(product)
                })
            }
            return map
        }, {})
    })

    const sectionsById = computed(() => arrayIndex(sections.value, 'ID'))
    const sectionsByCode = computed(() => arrayIndex(sections.value, 'CODE'))
    const sectionsTree = computed(() => treeBuild<ProductSection>(sections.value, "ID", "IBLOCK_SECTION_ID", "CHILDREN"))

    const sectionsPaths = computed(() => {
        return sections.value.reduce<Record<string, ProductSection[]>>((map, section) => {
            const path = []
            let currSection: ProductSection = section
            let currSectionId: number | null = section.ID
            while (true) {
                currSectionId = currSection.IBLOCK_SECTION_ID
                if (!currSectionId || !sectionsById.value[currSectionId]) {
                    break;
                }
                currSection = sectionsById.value[currSectionId]!
                path.push(currSection)
            }
            path.push(section)
            map[section.ID] = path
            return map
        }, {})
    })

    const productsBySection = computed(() => {
        return products.value.reduce<Record<string, Product[]>>((map, product) => {
            product.IBLOCK_SECTION_IDS.forEach((sectionId) => {
                if (!map[sectionId]) {
                    map[sectionId] = []
                }
                map[sectionId]!.push(product)
            })
            return map
        }, {})
    })

    const productFlagsById = computed(() => arrayIndex(productFlags.value, 'ID'))

    const productIngredientsById = computed(() => arrayIndex(productIngredients.value, 'ID'))

    const productFilterIngredientsById = computed(() => {
        const items = productIngredients.value.filter((item) => item.PROPERTIES?.SHOW_IN_FILTER)
        return arrayIndex(items, 'ID')
    })

    const resolveProduct = (product: ID | Product) => {
        if (typeof product === 'number' || typeof product === 'string') {
            return productsById.value[product]
        } else if (typeof product === 'object') {
            if (productsById.value[product.ID]) {
                return productsById.value[product.ID]
            }
            return product
        }
    }

    const resolveProductModel = (product: ProductDescriptor) => {
        if (typeof product === 'number' || typeof product === 'string') {
            return productsModelsById.value[product]
        } else if (typeof product === 'object') {
            if ('_isModel' in product) {
                return product
            } else {
                if (productsModelsById.value[product.ID]) {
                    return productsModelsById.value[product.ID]
                }
                return useProduct(product, loadGetters)
            }
        }
    }

    /**
     * Собирает FEATURE_IDS от всех родительских секций товара
     * Использует sectionsPaths для быстрого доступа к цепочке родителей
     * Requirements: 3.2
     */
    const collectSectionFeatureIds = (product: Product): number[] => {
        const featureIds: number[] = []

        product.IBLOCK_SECTION_IDS.forEach(sectionId => {
            const sectionPath = sectionsPaths.value[sectionId]
            if (sectionPath) {
                sectionPath.forEach(section => {
                    if (section.FEATURE_IDS) {
                        section.FEATURE_IDS.forEach(id => {
                            if (id !== null && id !== undefined) {
                                featureIds.push(id)
                            }
                        })
                    }
                })
            }
        })

        return featureIds
    }

    /**
     * Обогащает товары FEATURE_IDS от родительских секций
     * Requirements: 3.2
     */
    const enrichProductsWithSectionFeatures = (productsList: Product[]): Product[] => {
        return productsList.map(product => {
            const ownFeatureIds = product.FEATURE_IDS?.filter((id): id is number => id !== null && id !== undefined) || []
            const sectionFeatureIds = collectSectionFeatureIds(product)
            const allFeatureIds = [...new Set([...ownFeatureIds, ...sectionFeatureIds])]

            return {
                ...product,
                FEATURE_IDS: allFeatureIds
            }
        })
    }

    const fillProducts = (elements: Product[]) => {
        let productsToAdd = elements.filter(product => !productsById.value[product.ID])

        // Обогащаем товары FEATURE_IDS от секций (если sectionsPaths доступен)
        if (Object.keys(sectionsPaths.value).length > 0) {
            productsToAdd = enrichProductsWithSectionFeatures(productsToAdd)
        }

        products.value = [...products.value, ...productsToAdd]
    }

    const fillProduct = (element: Product) => {
        fillProducts([element])
    }

    const fetchProducts = async (ids: ID[]): Promise<ProductModel[]> => {
        return (await query(genqlBuild({
            catalog_product_list: {
                __fragment: 'ProductFields',
                __args: {
                    filter: {ID: {in: toIDS(ids)}}
                }
            }
        }), {}) || [])
    }

    const fetchProduct = async (id: ID): Promise<ProductModel | null> => {
        return query(genqlBuild({
            catalog_product_single: {
                __fragment: 'ProductFields',
                __args: {
                    filter: {ID: {eq: toID(id)}}
                }
            }
        }), {})
    }

    const productsLoader = async (
        ids: ProductDescriptor[],
    ): Promise<Maybe<ProductModel>[]> => {
        const missingIds = ids.filter((id) => {
            const element = resolveProductModel(id)
            return !element && (typeof id === 'number' || typeof id === 'string')
        }) as ID[]
        if (missingIds.length > 0) {
            const fetchedElements = await fetchProducts(missingIds)
            fillProducts(fetchedElements)
            await nextTick()
        }
        return ids.map((id) => resolveProductModel(id))
    }

    const ensureProduct = async (
        product: Maybe<ProductDescriptor>
    ): Promise<Maybe<ProductModel>> => {
        if (!product) return null;
        let result: Maybe<ProductModel> = resolveProductModel(product);
        if (result) {
            return result;
        } else {
            if (typeof product === 'number' || typeof product === 'string') {
                result = await fetchProduct(product)
                if (result) {
                    fillProduct(result)
                    await nextTick()
                    return productsModelsById.value[result.ID]
                }
            }
        }
    }

    const ensureProducts = async (ids: ProductDescriptor[]) => {
        return (await productsLoader(ids)).filter(Boolean) as ProductModel[]
    }

    const ensureProductsById = async (ids: ProductDescriptor[]): Promise<Record<string, Maybe<ProductModel>>> => {
        return arrayIndex(await ensureProducts(ids), 'ID')
    }

    const usedProducts = ref<Record<string, ProductModel>>({});

    /*
    const loader = useEntityDataloader<ProductModel>({
        fetchByIds: productsLoader,
        getStore: () => usedProducts.value,
        setStore: (entities) => {
            Object.assign(usedProducts.value, entities);
        },
        getId: (item) => item.ID,
    });
     */

    registerScopeQuery(
        STORE_NAME,
        "app",
        {
            catalog_product_all: {
                __fragment: "ProductFields",
            },
            catalog_product_section_list: {
                __fragment: "ProductSectionFields",
            },
            product_ingredient_list: {
                __fragment: "ProductIngredientFields",
            },
            product_modificator_list: {
                __fragment: "ModificatorFields",
            },
            product_flag_list: {
                __fragment: "ProductFlagFields",
            },
        },
        (data) => {
            // Сначала загружаем секции
            sections.value = data.catalog_product_section_list
            productIngredients.value = data.product_ingredient_list
            productFlags.value = data.product_flag_list

            // Загружаем товары с обогащением FEATURE_IDS от родительских секций
            fillProducts(data.catalog_product_all)
        }
    );

    const getProductsModelsByIds = (ids: number[]): ProductModel[] => {
        return ids.map((id) => productsModelsById.value[id]!).filter(Boolean)
    }

    const popularProducts = computed(() => {
        // Сортируем по количеству продаж
        const sortedByRating = [...productsModels.value]
            .filter(p => {
                // Исключаем товары с отрицательным HIT_POSITION
                if (p.HIT_POSITION && p.HIT_POSITION < 0) {
                    return false;
                }
                return p.SALES_COUNT && p.SALES_COUNT > 0;
            })
            .sort((a, b) => (b.SALES_COUNT || 0) - (a.SALES_COUNT || 0));

        // Разделяем на товары с HIT_POSITION и без
        const withPosition: ProductModel[] = [];
        const withoutPosition: ProductModel[] = [];

        sortedByRating.forEach(product => {
            if (product.HIT_POSITION && product.HIT_POSITION > 0) {
                withPosition.push(product);
            } else {
                withoutPosition.push(product);
            }
        });

        // Сортируем товары с HIT_POSITION по возрастанию позиции
        withPosition.sort((a, b) => (a.HIT_POSITION || 0) - (b.HIT_POSITION || 0));

        // Вставляем товары с HIT_POSITION на нужные позиции
        const result: ProductModel[] = [];
        let withoutPositionIndex = 0;

        // Проходим по всем возможным позициям
        for (let i = 0; i < sortedByRating.length; i++) {
            // Проверяем есть ли товар с позицией i+1 (позиции начинаются с 1)
            const productWithPosition = withPosition.find(p => p.HIT_POSITION === i + 1);

            if (productWithPosition) {
                // Вставляем товар с заданной позицией
                result.push(productWithPosition);
            } else {
                // Вставляем следующий товар без позиции
                if (withoutPositionIndex < withoutPosition.length) {
                    result.push(withoutPosition[withoutPositionIndex]);
                    withoutPositionIndex++;
                }
            }
        }

        // Добавляем оставшиеся товары с HIT_POSITION больше длины списка
        withPosition.forEach(product => {
            if (product.HIT_POSITION && product.HIT_POSITION > result.length && !result.includes(product)) {
                result.push(product);
            }
        });

        // Ограничиваем до 20 товаров
        return result.slice(0, 20);
    })

    /**
     * Популярные товары по секциям с учетом HIT_POSITION
     * Возвращает Record<sectionId, ProductModel[]>
     */
    const popularProductsBySection = computed(() => {
        const result: Record<string, ProductModel[]> = {};

        // Группируем товары по секциям
        const productsBySectionMap: Record<string, ProductModel[]> = {};

        productsModels.value.forEach(product => {
            // Исключаем товары с отрицательным HIT_POSITION
            if (product.HIT_POSITION && product.HIT_POSITION < 0) {
                return;
            }

            // Только товары с продажами
            if (!product.SALES_COUNT || product.SALES_COUNT <= 0) {
                return;
            }

            // Добавляем товар во все его секции
            product.IBLOCK_SECTION_IDS.forEach(sectionId => {
                if (!productsBySectionMap[sectionId]) {
                    productsBySectionMap[sectionId] = [];
                }
                productsBySectionMap[sectionId].push(product);
            });
        });

        // Для каждой секции применяем логику HIT_POSITION
        Object.keys(productsBySectionMap).forEach(sectionId => {
            const sectionProducts = productsBySectionMap[sectionId];

            // Сортируем по количеству продаж
            const sortedByRating = [...sectionProducts]
                .sort((a, b) => (b.SALES_COUNT || 0) - (a.SALES_COUNT || 0));

            // Разделяем на товары с HIT_POSITION и без
            const withPosition: ProductModel[] = [];
            const withoutPosition: ProductModel[] = [];

            sortedByRating.forEach(product => {
                if (product.HIT_POSITION && product.HIT_POSITION > 0) {
                    withPosition.push(product);
                } else {
                    withoutPosition.push(product);
                }
            });

            // Сортируем товары с HIT_POSITION по возрастанию позиции
            withPosition.sort((a, b) => (a.HIT_POSITION || 0) - (b.HIT_POSITION || 0));

            // Вставляем товары с HIT_POSITION на нужные позиции
            const sectionResult: ProductModel[] = [];
            let withoutPositionIndex = 0;

            // Проходим по всем возможным позициям
            for (let i = 0; i < sortedByRating.length; i++) {
                // Проверяем есть ли товар с позицией i+1 (позиции начинаются с 1)
                const productWithPosition = withPosition.find(p => p.HIT_POSITION === i + 1);

                if (productWithPosition) {
                    // Вставляем товар с заданной позицией
                    sectionResult.push(productWithPosition);
                } else {
                    // Вставляем следующий товар без позиции
                    if (withoutPositionIndex < withoutPosition.length) {
                        sectionResult.push(withoutPosition[withoutPositionIndex]);
                        withoutPositionIndex++;
                    }
                }
            }

            // Добавляем оставшиеся товары с HIT_POSITION больше длины списка
            withPosition.forEach(product => {
                if (product.HIT_POSITION && product.HIT_POSITION > sectionResult.length && !sectionResult.includes(product)) {
                    sectionResult.push(product);
                }
            });

            // Ограничиваем до 20 товаров
            result[sectionId] = sectionResult.slice(0, 20);
        });

        return result;
    })

    return {
        // loader,
        popularProducts,
        popularProductsBySection,
        usedProducts,
        products,
        productsById,
        productsModels,
        productsModelsById,
        productsModelsBySection,
        getProductsModelsByIds,
        sections,
        sectionsById,
        sectionsByCode,
        sectionsTree,
        sectionsPaths,
        productsBySection,
        productIngredients,
        productIngredientsById,
        productFilterIngredientsById,
        productFlags,
        productFlagsById,
        fillProduct,
        fillProducts,
        ensureProduct,
        ensureProducts,
        ensureProductsById,
        viewMode,
        setViewMode,
        isChangingViewMode
    };
});


